export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Forklift & Material Handling Equipment Atlas',
    subtitle: 'Visual Training for Forklifts, Pallet Trucks, Stackers & Warehouse Equipment',
    intro: [
      'Professional forklift training begins with understanding the equipment.',
      'Students need to recognize machine components, equipment categories, lifting structures, load-related concepts and the hazards present in warehouse environments.',
      'This volume gives instructors organized visual references for explaining essential forklift and material-handling knowledge.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Forklift fundamentals',
      'Counterbalance forklifts',
      'Reach trucks',
      'Powered pallet trucks',
      'Industrial stackers',
      'Forklift anatomy',
      'Forks and carriage assemblies',
      'Mast structures',
      'Hydraulic lifting components',
      'Operator compartments',
      'Counterweight awareness',
      'Steering and wheel systems',
      'Equipment data plates',
      'Load center concepts',
      'Load stability awareness',
      'Pallet characteristics',
      'Warehouse traffic organization',
      'Industrial storage areas',
      'Pedestrian separation',
      'Visibility hazards',
      'Equipment inspection awareness',
      'Machinery identification activities',
      'Warehouse scenarios',
      'Instructor observation exercises',
    ],
    result:
      'Give your students a clearer understanding of forklift equipment, lifting systems and warehouse safety through structured visual explanations.',
  },
  {
    number: 'Volume 02',
    title: 'Excavators & Earthmoving Equipment Atlas',
    subtitle: 'Visual Training for Excavators, Backhoes, Mini Excavators & Earthmoving Operations',
    intro: [
      'Excavators involve complex mechanical structures, hydraulic components and equipment-specific considerations.',
      'Explaining these subjects through text alone can be difficult.',
      'This volume transforms excavator knowledge into detailed visual references that instructors can use during professional training.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Hydraulic excavator anatomy',
      'Tracked excavators',
      'Compact excavators',
      'Backhoe loaders',
      'Boom components',
      'Stick structures',
      'Buckets and teeth',
      'Bucket linkages',
      'Hydraulic cylinders',
      'Hydraulic component awareness',
      'Equipment attachments',
      'Standard digging buckets',
      'Grading buckets',
      'Hydraulic breakers',
      'Grapple attachments',
      'Upper structures',
      'Cab and counterweights',
      'Track and undercarriage components',
      'Excavation-site organization',
      'Ground-condition awareness',
      'Underground utility hazards',
      'Work-area boundaries',
      'Machinery visibility',
      'Worker proximity',
      'Technical matching exercises',
      'Excavation scenario assessments',
    ],
    result:
      'Make excavator components, attachment systems and earthmoving hazards easier to visualize, explain and assess.',
  },
  {
    number: 'Volume 03',
    title: 'Bulldozers, Loaders & Grading Equipment Atlas',
    subtitle: 'Visual Training for Bulldozers, Wheel Loaders, Skid Steers & Motor Graders',
    intro: [
      'Not every construction machine serves the same purpose.',
      'Bulldozers, loaders, skid steers and motor graders have different structures, equipment characteristics and working environments.',
      'This volume helps instructors demonstrate those differences through detailed technical illustrations and realistic construction scenarios.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Bulldozer fundamentals',
      'Bulldozer anatomy',
      'Blade structures',
      'Track systems',
      'Undercarriage components',
      'Wheel loader anatomy',
      'Front buckets',
      'Loader arms',
      'Hydraulic cylinders',
      'Articulated steering awareness',
      'Front and rear chassis',
      'Skid steer loaders',
      'Track loaders',
      'Motor grader components',
      'Moldboard structures',
      'Grader frames',
      'Equipment silhouette comparisons',
      'Machinery category identification',
      'Attachment awareness',
      'Worksite visibility',
      'Blind-spot awareness',
      'Terrain considerations',
      'Multi-machine work environments',
      'Equipment recognition exercises',
      'Hazard-identification challenges',
    ],
    result:
      'Help your students recognize and understand major construction machinery categories through professional visual comparisons and technical references.',
  },
  {
    number: 'Volume 04',
    title: 'Cranes, Hoists & Lifting Equipment Atlas',
    subtitle: 'Visual Training for Mobile Cranes, Lifting Systems, Rigging Awareness & Load Safety',
    intro: [
      'Crane and lifting equipment involves sophisticated machinery structures, specialized components and equipment-specific limitations.',
      'Clear technical diagrams help instructors explain these concepts more effectively.',
      'This volume organizes major lifting-equipment subjects into visual instructor resources.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Mobile crane fundamentals',
      'Mobile crane anatomy',
      'Telescopic boom structures',
      'Boom components',
      'Crane carrier systems',
      'Counterweight awareness',
      'Outrigger components',
      'Hook blocks',
      'Hoist lines',
      'Wire rope identification',
      'Hoist drum components',
      'Tower cranes',
      'Overhead bridge cranes',
      'Hoist equipment',
      'Crane comparisons',
      'Lifting accessories',
      'Sling identification',
      'Shackles',
      'Lifting hooks',
      'Spreader beams',
      'Load-chart information awareness',
      'Equipment limitations',
      'Lifting-site organization',
      'Ground-condition awareness',
      'Work-area hazards',
      'Instructor identification challenges',
      'Technical reference assessments',
    ],
    result: 'Turn complex lifting equipment and crane components into professional visual teaching material.',
  },
  {
    number: 'Volume 05',
    title: 'Aerial Lifts & Elevated Work Platforms Atlas',
    subtitle: 'Visual Training for Scissor Lifts, Boom Lifts, Platform Safety & Elevated Work Hazards',
    intro: [
      'Elevated work platforms come in multiple designs, each with different structural characteristics and safety considerations.',
      'Students need to recognize those differences and understand the hazards present in elevated working environments.',
      'This volume gives instructors professional visual resources for teaching equipment recognition and elevated-workplace awareness.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Aerial platform fundamentals',
      'Scissor lift identification',
      'Scissor lift anatomy',
      'Platform structures',
      'Scissor mechanisms',
      'Articulating boom lifts',
      'Telescopic boom lifts',
      'Boom structures',
      'Platform components',
      'Chassis and support systems',
      'Hydraulic components',
      'Equipment comparisons',
      'Ground-condition awareness',
      'Stability considerations',
      'Overhead obstacles',
      'Electrical hazard awareness',
      'Pedestrian separation',
      'Industrial building cutaways',
      'Equipment-specific limitations',
      'Elevated-workplace scenarios',
      'Hazard-recognition activities',
      'Technical component exercises',
      'Instructor reference sheets',
      'Student assessments',
    ],
    result:
      'Help students understand aerial platform equipment and recognize elevated-workplace hazards through professional visual illustrations.',
  },
  {
    number: 'Volume 06',
    title: 'Road Construction & Compaction Equipment Atlas',
    subtitle: 'Visual Training for Road Rollers, Compactors, Asphalt Pavers & Roadwork Safety',
    intro: [
      'Road construction requires several machinery categories to operate within coordinated work environments.',
      'Understanding equipment differences, machine components and workplace hazards is essential for technical education.',
      'This volume combines road machinery references and construction-site awareness into one specialized visual atlas.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Road construction equipment fundamentals',
      'Vibratory rollers',
      'Single-drum rollers',
      'Tandem rollers',
      'Pneumatic-tire rollers',
      'Plate compactors',
      'Roller anatomy',
      'Drum components',
      'Chassis structures',
      'Asphalt pavers',
      'Material hoppers',
      'Conveyor components',
      'Screed assemblies',
      'Road equipment comparisons',
      'Compaction awareness',
      'Pavement layers',
      'Roadwork zones',
      'Construction site layouts',
      'Material delivery vehicles',
      'Machinery visibility',
      'Traffic organization',
      'Pedestrian separation',
      'Worksite access',
      'Ground conditions',
      'Roadwork hazards',
      'Equipment recognition exercises',
      'Instructor scenario assessments',
    ],
    result:
      'Make road construction machinery, equipment components and worksite safety concepts easier to explain through detailed visual references.',
  },
]
